import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, MessageSquare, Package } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface Dispute {
  id: string;
  order_id: string;
  reason: string;
  description: string;
  status: 'pending' | 'in_progress' | 'resolved' | 'rejected';
  admin_response: string | null;
  created_at: string;
  resolved_at: string | null;
  order?: {
    order_number: string;
    product?: {
      title: string;
      images: string[];
    };
  };
}

const DisputesManager = () => {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const { user } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    if (user) {
      fetchDisputes();
    }
  }, [user]);

  const fetchDisputes = async () => {
    try {
      const { data, error } = await supabase
        .from('order_disputes')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const disputesData = (data || []) as Dispute[];
      if (disputesData.length === 0) {
        setDisputes([]);
        return;
      }

      const orderIds = Array.from(new Set(disputesData.map((d) => d.order_id).filter(Boolean)));

      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          product:products(title, images)
        `)
        .in('id', orderIds);

      if (ordersError) {
        console.error('Error fetching linked orders for disputes:', ordersError);
      }

      const ordersMap = new Map(
        ((ordersData || []) as any[]).map((order) => [
          order.id,
          {
            order_number: order.order_number,
            product: order.product,
          },
        ])
      );

      const disputesWithOrders = disputesData.map((dispute) => ({
        ...dispute,
        order: ordersMap.get(dispute.order_id),
      }));

      setDisputes(disputesWithOrders);
    } catch (error) {
      console.error('Error fetching disputes:', error);
      toast.error(t('disputes.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const getReasonLabel = (reason: string) => {
    const labels: Record<string, string> = {
      defective_product: t('disputes.defectiveProduct'),
      wrong_item: t('disputes.wrongItem'),
      missing_item: t('disputes.missingItem'),
      damaged: t('disputes.damaged'),
      not_as_described: t('disputes.notAsDescribed'),
      other: t('disputes.other')
    };
    return labels[reason] || reason;
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: { variant: 'secondary' as const, label: t('disputes.pending') },
      in_progress: { variant: 'default' as const, label: t('disputes.inProgress') },
      resolved: { variant: 'default' as const, label: t('disputes.resolved') },
      rejected: { variant: 'destructive' as const, label: t('disputes.rejected') }
    };
    const config = variants[status as keyof typeof variants] || variants.pending;
    return <Badge variant={config.variant} className="text-[10px] md:text-xs whitespace-nowrap">{config.label}</Badge>;
  };

  if (loading) {
    return <div className="animate-pulse">{t('messaging.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">{t('disputes.title')}</h2>
        <p className="text-muted-foreground">{t('disputes.description')}</p>
      </div>

      <div className="grid gap-4">
        {disputes.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t('disputes.noDisputes')}</p>
            </CardContent>
          </Card>
        ) : (
          disputes.map((dispute) => (
            <Card key={dispute.id}>
              <CardHeader className="p-3 md:p-6">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      {dispute.order?.product?.images?.[0] ? (
                        <img
                          src={dispute.order.product.images[0]}
                          alt={dispute.order?.product?.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Package className="h-full w-full p-2 md:p-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-xs md:text-base truncate">
                        {t('disputes.order')} {dispute.order?.order_number}
                      </CardTitle>
                      <CardDescription className="text-xs truncate">
                        {dispute.order?.product?.title}
                      </CardDescription>
                      <div className="flex items-center gap-1 md:gap-2 mt-1 md:mt-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] md:text-xs truncate max-w-[120px]">{getReasonLabel(dispute.reason)}</Badge>
                        {getStatusBadge(dispute.status)}
                      </div>
                    </div>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedDispute(dispute)}
                        className="h-7 md:h-9 px-2 md:px-3 flex-shrink-0"
                      >
                        <MessageSquare className="h-3 w-3 md:h-4 md:w-4 md:mr-2" />
                        <span className="hidden md:inline">{t('disputes.details')}</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>{t('disputes.detailsTitle')}</DialogTitle>
                        <DialogDescription>
                          {t('disputes.order')} {dispute.order?.order_number}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>{t('disputes.reason')}</Label>
                          <p className="text-sm">{getReasonLabel(dispute.reason)}</p>
                        </div>
                        <div>
                          <Label>{t('disputes.description_label')}</Label>
                          <p className="text-sm text-muted-foreground">{dispute.description}</p>
                        </div>
                        <div>
                          <Label>{t('disputes.status')}</Label>
                          <div className="mt-2">{getStatusBadge(dispute.status)}</div>
                        </div>
                        {dispute.admin_response && (
                          <div>
                            <Label>{t('disputes.adminResponse')}</Label>
                            <Card className="mt-2">
                              <CardContent className="p-4">
                                <p className="text-sm">{dispute.admin_response}</p>
                              </CardContent>
                            </Card>
                          </div>
                        )}
                        <div>
                          <Label>{t('disputes.createdAt')}</Label>
                          <p className="text-sm text-muted-foreground">
                            {new Date(dispute.created_at).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        {dispute.resolved_at && (
                          <div>
                            <Label>{t('disputes.resolvedAt')}</Label>
                            <p className="text-sm text-muted-foreground">
                              {new Date(dispute.resolved_at).toLocaleDateString('fr-FR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default DisputesManager;
